<template>
  <div>
    <!-- Connection & Subject -->
    <b-card
      no-body
      class="mb-1"
    >
      <b-card-body class="pb-0">
        <b-row>
          <b-col md="3">
            <b-form-group
              label="From Mail"
              label-size="sm"
            >
              <b-form-select
                v-model="form.connectionName"
                size="sm"
                :options="connectionOptions"
              >
                <template #first>
                  <b-form-select-option
                    :value="''"
                    disabled
                  >
                    Select connection...
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Subject"
              label-size="sm"
            >
              <b-form-input
                v-model="form.subject"
                size="sm"
                placeholder="Email subject line..."
                trim
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group
              label="Attachments"
              label-size="sm"
            >
              <b-form-file
                v-model="form.attachments"
                size="sm"
                multiple
                placeholder="Choose files..."
                drop-placeholder="Drop files here..."
              />
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Attachment chips -->
        <div
          v-if="form.attachments && form.attachments.length"
          class="mb-1"
        >
          <b-badge
            v-for="(file, idx) in form.attachments"
            :key="idx"
            variant="light-primary"
            class="mr-50 mb-25 cursor-pointer"
            @click="removeAttachment(idx)"
          >
            <feather-icon
              icon="FileIcon"
              size="12"
              class="mr-25"
            />
            {{ file.name }}
            <feather-icon
              icon="XIcon"
              size="12"
              class="ml-25"
            />
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <b-row>
      <!-- Left: Recipient Selector -->
      <b-col lg="5">
        <recipient-selector
          :selected-recipients.sync="selectedRecipients"
          :include-cc.sync="includeCc"
        />
      </b-col>

      <!-- Right: Email Editor -->
      <b-col lg="7">
        <b-card no-body>
          <email-editor
            ref="emailEditor"
            v-model="form.htmlContent"
            :signature-config="signatureConfig"
          />
        </b-card>

        <!-- Send Bar -->
        <div class="d-flex align-items-center justify-content-between mt-1">
          <div>
            <b-badge
              v-if="selectedRecipients.length"
              variant="light-primary"
              pill
            >
              {{ selectedRecipients.length }} recipient{{ selectedRecipients.length > 1 ? 's' : '' }} selected
            </b-badge>
            <span
              v-else
              class="font-small-3 text-muted"
            >No recipients selected</span>
          </div>
          <b-button
            variant="primary"
            :disabled="!canSend || sending"
            @click="sendEmails"
          >
            <b-spinner
              v-if="sending"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SendIcon"
              size="14"
              class="mr-50"
            />
            {{ sending ? 'Sending...' : 'Send Emails' }}
          </b-button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard, BCardBody, BRow, BCol, BFormGroup, BFormInput,
  BFormFile, BBadge, BButton, BSpinner,
  BFormSelect, BFormSelectOption,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import RecipientSelector from '@/components/Communication/BulkMailing/RecipientSelector.vue'
import EmailEditor from '@/components/Communication/BulkMailing/EmailEditor.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormFile,
    BBadge,
    BButton,
    BSpinner,
    BFormSelect,
    BFormSelectOption,
    RecipientSelector,
    EmailEditor,
  },

  data() {
    return {
      form: {
        connectionName: '',
        subject: '',
        htmlContent: '',
        attachments: [],
      },
      includeCc: false,
      connectionOptions: [],
      selectedRecipients: [],
      sending: false,
      signatureConfig: {
        name: 'Your Name',
        title: 'Manager',
        company: 'Your Company',
        phone: '+91 98765 43210',
        email: 'info@yourcompany.com',
        website: 'www.yourcompany.com',
        address: 'Ahmedabad, Gujarat',
      },
    }
  },

  computed: {
    canSend() {
      return (
        this.form.connectionName.trim()
        && this.form.subject.trim()
        && this.form.htmlContent.trim()
        && this.selectedRecipients.length > 0
      )
    },

    recipientsPayload() {
      return this.selectedRecipients.map(user => ({
        to: this.getParsedEmails(user.email),
        cc: this.includeCc ? this.getParsedEmails(user.cc_emails) : [],
        name: user.identifier || user.name || '',
      }))
    },
  },

  created() {
    this.fetchConnectionNames()

    const { query } = this.$route
    if (query.connection_name) {
      this.form.connectionName = query.connection_name
    }
    if (query.subject) {
      this.form.subject = query.subject
    }
    if (query.content) {
      this.form.htmlContent = query.content
    }
  },

  methods: {
    async fetchConnectionNames() {
      try {
        const { data } = await axios.get('/emails/connection_names')
        this.connectionOptions = data.map(item => ({
          value: typeof item === 'string' ? item : item.name,
          text: typeof item === 'string'
            ? `${item}@vatikadistributers.com`
            : `${item.name}@vatikadistributers.com`,
        }))
      } catch {
        this.connectionOptions = []
      }
    },
    getParsedEmails(raw) {
      if (!raw) return []
      if (Array.isArray(raw)) return raw.filter(Boolean)
      const str = String(raw).trim()
      if (str === '[]' || str === '') return []
      if (str.startsWith('[')) {
        try {
          return JSON.parse(str.replace(/'/g, '"')).filter(Boolean)
        } catch {
          return str.match(/[\w.-]+@[\w.-]+\.\w+/g) || []
        }
      }
      return str.split(',').map(e => e.trim()).filter(Boolean)
    },
    removeAttachment(idx) {
      const files = [...this.form.attachments]
      files.splice(idx, 1)
      this.form.attachments = files
    },

    async sendEmails() {
      if (!this.canSend) return

      this.sending = true
      try {
        const formData = new FormData()
        formData.append('connection_name', this.form.connectionName.trim())
        formData.append('subject', this.form.subject.trim())
        formData.append('html_content', this.form.htmlContent)
        formData.append('recipients', JSON.stringify(this.recipientsPayload))

        if (this.form.attachments && this.form.attachments.length) {
          this.form.attachments.forEach(file => {
            formData.append('attachments', file)
          })
        }

        await axios.post('/emails/send_bulk_emails', formData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `Emails queued for ${this.selectedRecipients.length} recipients!`,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const msg = error?.response?.data?.message
          || error?.response?.data?.error
          || 'Failed to send emails'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.sending = false
      }
    },
  },
}
</script>
